import { useCallback, useEffect, useState } from "react";

import logger from "~/helpers/logger";
import fetchTenantIdByCompanySlug from "~portal/helpers/vanityTenantApi";

export type VanityTenantResolution =
	| { status: "loading" }
	| { status: "notFound" }
	| { status: "error"; retry: () => void }
	| { status: "resolved"; tenantId: string };

const useVanityTenantResolution = (slug: string): VanityTenantResolution => {
	const [resolution, setResolution] = useState<VanityTenantResolution>({ status: "loading" });
	const [attempt, setAttempt] = useState(0);

	const retry = useCallback(() => setAttempt((previousAttempt) => previousAttempt + 1), []);

	useEffect(() => {
		let isCurrent = true;
		setResolution({ status: "loading" });
		(async () => {
			try {
				const tenantId = await fetchTenantIdByCompanySlug(slug);
				if (!isCurrent) return;
				setResolution(tenantId ? { status: "resolved", tenantId } : { status: "notFound" });
			} catch (resolveErrorCause) {
				if (!isCurrent) return;
				logger.error("Failed to resolve vanity portal tenant slug", resolveErrorCause);
				setResolution({ status: "error", retry });
			}
		})();
		return () => {
			isCurrent = false;
		};
	}, [slug, attempt, retry]);

	return resolution;
};

export default useVanityTenantResolution;
